export const REPORT_MODES = [
  { value: 'day', label: '일일 조회' },
  { value: 'week', label: '주간 조회' },
  { value: 'month', label: '월간 조회' },
];

const pad = (value) => String(value).padStart(2, '0');

export function formatDate(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function parseDate(value) {
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  const [year, month, day] = String(value).split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function addDays(date, amount) {
  const next = parseDate(date);
  next.setDate(next.getDate() + amount);
  return next;
}

export function getPeriodRange(mode, anchorDate) {
  const anchor = parseDate(anchorDate);
  let start = anchor;
  let end = anchor;

  if (mode === 'week') {
    const mondayOffset = anchor.getDay() === 0 ? -6 : 1 - anchor.getDay();
    start = addDays(anchor, mondayOffset);
    end = addDays(start, 6);
  }

  if (mode === 'month') {
    start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  }

  return {
    start,
    end,
    startDate: formatDate(start),
    endDate: formatDate(end),
  };
}

export function moveAnchorDate(mode, anchorDate, direction) {
  const anchor = parseDate(anchorDate);
  if (mode === 'day') return addDays(anchor, direction);
  if (mode === 'week') return addDays(anchor, direction * 7);
  return new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1);
}

export function getPeriodTitle(mode, range) {
  if (mode === 'day') {
    return `${range.start.getFullYear()}년 ${range.start.getMonth() + 1}월 ${range.start.getDate()}일`;
  }
  if (mode === 'week') return `${range.startDate} ~ ${range.endDate}`;
  return `${range.start.getFullYear()}년 ${range.start.getMonth() + 1}월`;
}

function toNumber(value) {
  if (value === null || value === undefined || value === '' || value === 'NULL') return 0;
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

export function normalizeReportRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => ({
    ...row,
    assignedTestCount: toNumber(row.assignedTestCount),
    testScoreCount: toNumber(row.testScoreCount),
    testScoreSum: toNumber(row.testScoreSum),
    testAverage: row.testAverage === 'NULL' || row.testAverage === null
      ? null
      : toNumber(row.testAverage),
  }));
}

export function getAttendanceStatus(row) {
  if (!row) return { key: 'none', label: '수업 없음' };
  if (row.attendanceCheckedyn === 'Y') return { key: 'present', label: '출석' };
  if (row.attendanceCheckedyn === 'L') return { key: 'late', label: '지각' };
  return { key: 'absent', label: '결석' };
}

function getRate(completed, total) {
  if (!total) return null;
  return Math.round((completed / total) * 100);
}

export function summarizeReport(rows) {
  const classDays = rows.length;
  const present = rows.filter((row) => row.attendanceCheckedyn === 'Y').length;
  const late = rows.filter((row) => row.attendanceCheckedyn === 'L').length;
  const absent = Math.max(classDays - present - late, 0);
  const progressCompleted = rows.filter((row) => row.progressCheckedyn === 'Y').length;
  const homeworkRows = rows.filter((row) => row.homeworkAssigned === 'Y');
  const homeworkCompleted = homeworkRows.filter((row) => row.homeworkCheckedyn === 'Y').length;
  const testScoreCount = rows.reduce((sum, row) => sum + row.testScoreCount, 0);
  const testScoreSum = rows.reduce((sum, row) => sum + row.testScoreSum, 0);

  return {
    classDays,
    attendance: {
      completed: present,
      total: classDays,
      rate: getRate(present, classDays),
      present,
      late,
      absent,
    },
    progress: {
      completed: progressCompleted,
      total: classDays,
      rate: getRate(progressCompleted, classDays),
    },
    homework: {
      completed: homeworkCompleted,
      total: homeworkRows.length,
      rate: getRate(homeworkCompleted, homeworkRows.length),
    },
    tests: {
      completed: testScoreCount,
      total: rows.reduce((sum, row) => sum + row.assignedTestCount, 0),
      average: testScoreCount ? Math.round((testScoreSum / testScoreCount) * 10) / 10 : null,
    },
  };
}

export function createVisibleDays(mode, anchorDate) {
  const range = getPeriodRange(mode, anchorDate);
  if (mode === 'day') return [range.start];
  if (mode === 'week') return Array.from({ length: 7 }, (_, index) => addDays(range.start, index));

  const mondayOffset = range.start.getDay() === 0 ? -6 : 1 - range.start.getDay();
  const gridStart = addDays(range.start, mondayOffset);
  return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
}

export function createInsights(summary, rows) {
  if (!summary.classDays) {
    return [{ tone: 'info', text: '선택한 기간에는 진도가 등록된 수업일이 없습니다.' }];
  }

  const insights = [];
  if (summary.attendance.rate === 100) {
    insights.push({ tone: 'good', text: '선택한 기간의 모든 수업에 출석했습니다.' });
  } else if (summary.attendance.rate < 80) {
    insights.push({ tone: 'warning', text: `출석률이 ${summary.attendance.rate}%입니다. 결석한 수업일을 확인해 주세요.` });
  } else {
    insights.push({ tone: 'good', text: `출석률 ${summary.attendance.rate}%로 꾸준히 참여하고 있습니다.` });
  }

  if (summary.progress.rate !== null && summary.progress.rate < 100) {
    insights.push({ tone: 'info', text: `완료하지 않은 진도가 ${summary.progress.total - summary.progress.completed}건 있습니다.` });
  }
  if (summary.homework.total && summary.homework.rate < 100) {
    insights.push({ tone: 'warning', text: `미완료 과제가 ${summary.homework.total - summary.homework.completed}건 있습니다.` });
  }
  if (summary.tests.average !== null) {
    const scoredDates = rows.filter((row) => row.testScoreCount > 0).length;
    insights.push({ tone: 'test', text: `OMR 채점 ${summary.tests.completed}건의 평균은 ${summary.tests.average}점입니다. (${scoredDates}개 수업일)` });
  }
  return insights.slice(0, 4);
}
